import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Stack,
  Card,
  Chip,
  Slider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider,
  Alert,
  alpha,
  useTheme,
  Paper,
} from '@mui/material';
import {
  Calculator,
  MousePointerClick,
  BarChart3,
  Sliders,
  Save,
  RotateCcw,
  Zap,
  Globe,
  TrendingUp,
  Sparkles,
  DollarSign,
} from 'lucide-react';
import axios from '../utils/axios';

const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
};

const TRAFFIC_PRESETS = [
  { label: '25K', value: 25000 },
  { label: '100K', value: 100000 },
  { label: '250K', value: 250000 },
  { label: '500K', value: 500000 },
  { label: '1M', value: 1000000 },
  { label: '2.5M', value: 2500000 },
];

const HIGH_CPC_NICHES = [
  { name: 'Industrial Machinery & CNC', cpc: '₹28 - ₹45', intent: 'High Commercial Intent' },
  { name: 'Solar Panels & Inverters', cpc: '₹22 - ₹36', intent: 'Renewable B2B' },
  { name: 'Packaging, Corrugation & Boxes', cpc: '₹16 - ₹28', intent: 'E-commerce Supply' },
  { name: 'Commercial Vehicles & Transporters', cpc: '₹15 - ₹25', intent: 'Logistics Fleet' },
  { name: 'Chemicals, Polymers & Resins', cpc: '₹14 - ₹24', intent: 'Bulk Manufacturing' },
  { name: 'Textile Mills, Yarn & Fabrics', cpc: '₹10 - ₹18', intent: 'Apparel Wholesale' },
];

export default function AdSenseRevenueSection({ adsenseData = {}, onSettingsUpdated }) {
  const theme = useTheme();

  // Local calculation states initialized from backend / settings
  const [views, setViews] = useState(adsenseData?.monthlyPageViews || 100000);
  const [units, setUnits] = useState(adsenseData?.adUnits || 2);
  const [ctr, setCtr] = useState(adsenseData?.ctr || 1.5);
  const [cpc, setCpc] = useState(adsenseData?.cpc || 12);
  const [includeInGross, setIncludeInGross] = useState(Boolean(adsenseData?.isIncludedInGross));
  const [actualRevenue, setActualRevenue] = useState(adsenseData?.actualMonthlySet || 0);

  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Keep in sync when parent data updates
  useEffect(() => {
    if (adsenseData?.monthlyPageViews) setViews(adsenseData.monthlyPageViews);
    if (adsenseData?.adUnits) setUnits(adsenseData.adUnits);
    if (adsenseData?.ctr !== undefined) setCtr(adsenseData.ctr);
    if (adsenseData?.cpc !== undefined) setCpc(adsenseData.cpc);
    if (adsenseData?.isIncludedInGross !== undefined) setIncludeInGross(adsenseData.isIncludedInGross);
    if (adsenseData?.actualMonthlySet !== undefined) setActualRevenue(adsenseData.actualMonthlySet);
  }, [adsenseData]);

  // Derived calculation values
  const calc = useMemo(() => {
    const totalViews = Math.max(1, Number(views) || 0);
    const totalUnits = Math.max(1, Number(units) || 1);
    const totalImpressions = totalViews * totalUnits;
    const clickRate = Math.max(0, Number(ctr) || 0) / 100;
    const estimatedClicks = Math.round(totalImpressions * clickRate);
    const costPerClick = Math.max(0, Number(cpc) || 0);
    const calculatedMonthly = Math.round(estimatedClicks * costPerClick);

    // If an actual monthly override is set and > 0, use it; otherwise use the calculated formula
    const effectiveMonthly = Number(actualRevenue) > 0 ? Number(actualRevenue) : calculatedMonthly;
    const dailyEarnings = Math.round(effectiveMonthly / 30);
    const yearlyEarnings = Math.round(effectiveMonthly * 12);

    const pageRpm = totalViews > 0 ? ((effectiveMonthly / totalViews) * 1000).toFixed(1) : 0;
    const impressionRpm = totalImpressions > 0 ? ((effectiveMonthly / totalImpressions) * 1000).toFixed(1) : 0;

    return {
      totalImpressions,
      estimatedClicks,
      calculatedMonthly,
      effectiveMonthly,
      dailyEarnings,
      yearlyEarnings,
      pageRpm,
      impressionRpm,
    };
  }, [views, units, ctr, cpc, actualRevenue]);

  const handleSaveAssumptions = async () => {
    setSaving(true);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      await axios.put('/admin/revenue/settings', {
        adsense_page_views: Number(views),
        adsense_ad_units: Number(units),
        adsense_ctr: Number(ctr),
        adsense_cpc: Number(cpc),
        adsense_revenue_monthly: Number(actualRevenue) || 0,
        adsense_include_in_gross: includeInGross,
      });
      setSuccessMsg('AdSense monetization settings saved successfully! Revenue overview updated.');
      if (typeof onSettingsUpdated === 'function') {
        onSettingsUpdated();
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.msg || 'Failed to save AdSense settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setViews(100000);
    setUnits(2);
    setCtr(1.5);
    setCpc(12);
    setActualRevenue(0);
  };

  const handleUseRecordedViews = () => {
    if (adsenseData?.recordedProductViews > 0) {
      setViews(adsenseData.recordedProductViews);
    }
  };

  return (
    <Card
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 3,
        mb: 3,
        border: `1px solid ${alpha('#10b981', 0.25)}`,
        background: `linear-gradient(180deg, ${alpha('#10b981', 0.03)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative top icon watermark */}
      <Box sx={{ position: 'absolute', top: -30, right: -20, opacity: 0.04, color: '#10b981', pointerEvents: 'none' }}>
        <Calculator size={180} />
      </Box>

      {/* Section Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: alpha('#10b981', 0.15),
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Globe size={20} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: 'text.primary' }}>
              Google AdSense &amp; Display Ad Revenue
            </Typography>
            <Chip
              label="B2B Traffic Monetization"
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11,
                bgcolor: alpha('#10b981', 0.12),
                color: '#047857',
              }}
            />
          </Stack>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Interactive projection calculator for estimating ad earnings, page RPM, and ad-slot yields on Reseller Bazzar.
          </Typography>
        </Box>

        {/* Global Inclusion Toggle */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInGross}
                onChange={(e) => setIncludeInGross(e.target.checked)}
                color="success"
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  Include in Gross Revenue &amp; EBITDA
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {includeInGross ? 'Contributing to EBITDA margin' : 'Tracked as standalone ad stream'}
                </Typography>
              </Box>
            }
          />
        </Stack>
      </Stack>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {/* KPI Cards: Live Earnings Projections */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: 'Daily Estimated Revenue',
            value: fmtINR(calc.dailyEarnings),
            sub: `${calc.estimatedClicks ? Math.round(calc.estimatedClicks / 30) : 0} clicks / day`,
            color: '#059669',
            icon: <Zap size={18} />,
          },
          {
            label: 'Monthly Estimated Revenue',
            value: fmtINR(calc.effectiveMonthly),
            sub: Number(actualRevenue) > 0 ? 'Actual payout configured' : `~${fmtShort(calc.calculatedMonthly)} calculated`,
            color: '#10b981',
            icon: <TrendingUp size={18} />,
            highlight: true,
          },
          {
            label: 'Annual Projected Revenue',
            value: fmtShort(calc.yearlyEarnings),
            sub: `${fmtINR(calc.yearlyEarnings)} / year run-rate`,
            color: theme.palette.primary.main,
            icon: <DollarSign size={18} />,
          },
          {
            label: 'Page RPM (Revenue / 1K views)',
            value: `₹${calc.pageRpm}`,
            sub: `Impression RPM: ₹${calc.impressionRpm}`,
            color: '#8b5cf6',
            icon: <BarChart3 size={18} />,
          },
        ].map((card, idx) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: `1px solid ${card.highlight ? alpha(card.color, 0.4) : theme.palette.divider}`,
                bgcolor: card.highlight ? alpha(card.color, 0.05) : 'background.paper',
                position: 'relative',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Box sx={{ color: card.color }}>{card.icon}</Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>
                  {card.label}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: card.color, lineHeight: 1.2 }}>
                {card.value}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5, fontWeight: 600 }}>
                {card.sub}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Main Interactive Controls & Settings */}
      <Grid container spacing={3}>
        {/* Left Side: Interactive Sliders & Presets */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.background.paper, 0.7),
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Sliders size={18} color="#059669" />
              <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                Traffic &amp; Monetization Parameter Controls
              </Typography>
            </Stack>

            {/* Parameter 1: Monthly Page Views */}
            <Box sx={{ mb: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Monthly Page Views</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {adsenseData?.recordedProductViews > 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleUseRecordedViews}
                      sx={{ fontSize: 11, fontWeight: 700, py: 0, color: '#059669' }}
                    >
                      Use Recorded ({fmtShort(adsenseData.recordedProductViews)})
                    </Button>
                  )}
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>
                    {Number(views).toLocaleString('en-IN')} views
                  </Typography>
                </Stack>
              </Stack>

              <Slider
                value={Number(views) || 10000}
                min={10000}
                max={2500000}
                step={10000}
                onChange={(_, val) => setViews(val)}
                sx={{
                  color: '#10b981',
                  '& .MuiSlider-thumb': { boxShadow: '0 2px 8px rgba(16,185,129,0.3)' },
                }}
              />

              {/* Quick Presets */}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', alignSelf: 'center', mr: 0.5 }}>
                  Presets:
                </Typography>
                {TRAFFIC_PRESETS.map((p) => (
                  <Chip
                    key={p.label}
                    label={p.label}
                    size="small"
                    onClick={() => setViews(p.value)}
                    variant={views === p.value ? 'filled' : 'outlined'}
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: views === p.value ? '#10b981' : 'transparent',
                      color: views === p.value ? '#fff' : 'text.primary',
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Parameter 2 & 3: Ad Units & CTR in 2 Columns */}
            <Grid container spacing={2.5} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Ad Units / Page</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#059669' }}>
                    {units} {units === 1 ? 'Slot' : 'Slots'}
                  </Typography>
                </Stack>
                <Slider
                  value={Number(units) || 2}
                  min={1}
                  max={5}
                  step={1}
                  marks
                  onChange={(_, val) => setUnits(val)}
                  sx={{ color: '#10b981' }}
                />
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  Reseller Bazzar active units: TopBanner &amp; SquareAds
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Click-Through Rate (CTR)</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#059669' }}>
                    {ctr}%
                  </Typography>
                </Stack>
                <Slider
                  value={Number(ctr) || 1.5}
                  min={0.2}
                  max={5.0}
                  step={0.1}
                  onChange={(_, val) => setCtr(val)}
                  sx={{ color: '#10b981' }}
                />
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                  Indian B2B classifieds benchmark: 1.2% - 2.2%
                </Typography>
              </Grid>
            </Grid>

            {/* Parameter 4: Average CPC */}
            <Box sx={{ mb: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Average Cost-Per-Click (CPC)</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>
                  ₹{cpc} per click
                </Typography>
              </Stack>
              <Slider
                value={Number(cpc) || 12}
                min={2}
                max={50}
                step={1}
                onChange={(_, val) => setCpc(val)}
                sx={{ color: '#10b981' }}
              />
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                B2B machinery/industrial suppliers command higher CPCs (₹18–₹40) than generic ads.
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Actual AdSense Override Field */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.25 }}>
                  Actual Google AdSense Payout (Optional Override)
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  If set &gt; 0, this actual statement figure is used in P&amp;L and EBITDA instead of the calculated formula.
                </Typography>
              </Box>
              <TextField
                size="small"
                type="number"
                placeholder="0"
                value={actualRevenue || ''}
                onChange={(e) => setActualRevenue(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={{ width: { xs: '100%', sm: 160 } }}
              />
            </Stack>

            {/* Save & Reset Actions */}
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RotateCcw size={14} />}
                onClick={handleReset}
                sx={{ fontWeight: 600, color: 'text.secondary', borderColor: theme.palette.divider }}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Save size={14} />}
                disabled={saving}
                onClick={handleSaveAssumptions}
                sx={{
                  bgcolor: '#059669',
                  '&:hover': { bgcolor: '#047857' },
                  fontWeight: 700,
                  px: 2.5,
                }}
              >
                {saving ? 'Saving...' : 'Save Monetization Settings'}
              </Button>
            </Stack>
          </Paper>
        </Grid>

        {/* Right Side: Funnel Breakdown & High-Yield B2B Niches */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={2.5}>
            {/* Funnel Calculation Summary Box */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 13.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Sparkles size={16} color="#059669" />
                Monthly Ad Monetization Formula
              </Typography>

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Total Page Views:</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                    {Number(views).toLocaleString('en-IN')}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Ad Units Per Page:</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>× {units}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Total Ad Impressions:</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#059669' }}>
                    {calc.totalImpressions.toLocaleString('en-IN')}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Estimated Clicks (CTR {ctr}%):</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#059669' }}>
                    {calc.estimatedClicks.toLocaleString('en-IN')} clicks
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Average CPC:</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>₹{cpc}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 800 }}>Projected Monthly Ad Revenue:</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>
                    {fmtINR(calc.effectiveMonthly)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* B2B Keyword Benchmark Guide */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha('#10b981', 0.02),
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MousePointerClick size={15} color="#059669" />
                High-Yield B2B Niches on Reseller Bazzar
              </Typography>
              <Stack spacing={0.75}>
                {HIGH_CPC_NICHES.map((n, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderRadius: 1.5,
                      bgcolor: i % 2 === 0 ? alpha(theme.palette.divider, 0.2) : 'transparent',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700 }} noWrap>
                        {n.name}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        {n.intent}
                      </Typography>
                    </Box>
                    <Chip
                      label={n.cpc}
                      size="small"
                      sx={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        height: 20,
                        bgcolor: alpha('#10b981', 0.15),
                        color: '#047857',
                      }}
                    />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}

